import { createClient } from '@supabase/supabase-js';

// ============================================
// SUPABASE CONNECTION CONFIGURATION
// ============================================
// Environment variables are loaded from .env file
// In React, environment variables must be prefixed with REACT_APP_
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Create a mock client for development when credentials are not available
const createMockClient = () => {
    console.warn('⚠️ Supabase credentials not found. Running in offline mode with mock data.');

    // Store auth callbacks for triggering state changes
    let authCallbacks = [];
    let currentUser = null;

    // Create chainable query builder
    const createQueryBuilder = () => {
        const builder = {
            select: () => builder,
            insert: () => builder,
            update: () => builder,
            delete: () => builder,
            upsert: () => builder,
            eq: () => builder,
            neq: () => builder,
            gt: () => builder,
            gte: () => builder,
            lt: () => builder,
            lte: () => builder,
            like: () => builder,
            ilike: () => builder,
            is: () => builder,
            in: () => builder,
            contains: () => builder,
            containedBy: () => builder,
            rangeGt: () => builder,
            rangeGte: () => builder,
            rangeLt: () => builder,
            rangeLte: () => builder,
            rangeAdjacent: () => builder,
            overlaps: () => builder,
            textSearch: () => builder,
            match: () => builder,
            not: () => builder,
            or: () => builder,
            filter: () => builder,
            order: () => builder,
            limit: () => builder,
            range: () => builder,
            single: () => Promise.resolve({ data: null, error: null }),
            maybeSingle: () => Promise.resolve({ data: null, error: null }),
            then: (resolve) => resolve({ data: [], error: null })
        };
        return builder;
    };

    return {
        from: (table) => createQueryBuilder(),
        rpc: () => Promise.resolve({ data: [], error: null }),
        auth: {
            signInWithPassword: (credentials) => {
                // Mock successful login
                const user = {
                    id: 'mock-user-id',
                    email: credentials.email,
                    email_confirmed_at: new Date().toISOString()
                };
                const session = {
                    user: user,
                    access_token: 'mock-token'
                };
                currentUser = user;

                // Trigger all registered auth callbacks
                setTimeout(() => {
                    authCallbacks.forEach(callback => callback('SIGNED_IN', session));
                }, 0);

                return Promise.resolve({
                    data: { user, session },
                    error: null
                });
            },
            signUp: ({ email, password }) => {
                const user = {
                    id: 'mock-user-id',
                    email,
                    email_confirmed_at: null
                };
                const session = null;
                return Promise.resolve({
                    data: { user, session },
                    error: null
                });
            },
            signInWithOAuth: ({ provider }) => {
                const user = {
                    id: 'mock-user-id',
                    email: `mock-${provider}-user@example.com`,
                    email_confirmed_at: new Date().toISOString()
                };
                const session = {
                    user,
                    access_token: 'mock-token'
                };
                currentUser = user;

                setTimeout(() => {
                    authCallbacks.forEach(callback => callback('SIGNED_IN', session));
                }, 0);

                return Promise.resolve({
                    data: { provider, url: null },
                    error: null
                });
            },
            signOut: () => {
                currentUser = null;
                setTimeout(() => {
                    authCallbacks.forEach(callback => callback('SIGNED_OUT', null));
                }, 0);
                return Promise.resolve({ error: null });
            },
            getSession: () => Promise.resolve({
                data: { session: currentUser ? { user: currentUser } : null },
                error: null
            }),
            getUser: () => Promise.resolve({
                data: { user: currentUser },
                error: null
            }),
            onAuthStateChange: (callback) => {
                authCallbacks.push(callback);
                // Immediately trigger with current state
                setTimeout(() => callback(currentUser ? 'SIGNED_IN' : 'SIGNED_OUT',
                    currentUser ? { user: currentUser } : null), 0);
                return {
                    data: {
                        subscription: {
                            unsubscribe: () => {
                                authCallbacks = authCallbacks.filter(cb => cb !== callback);
                            }
                        }
                    }
                };
            }
        }
    };
};

// Create Supabase client or mock client.
// BL-177 Step 2d screenshot harness: a dev-server run with REACT_APP_STEP2D_PREVIEW=1 swaps in an in-memory fixture
// client (src/dev/fixtureSupabase.js). The NODE_ENV check is a build-time constant, so a production build drops this
// branch and never bundles the fixture.
export const supabaseClient = (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_STEP2D_PREVIEW === '1')
    // eslint-disable-next-line global-require
    ? require('../dev/fixtureSupabase').fixtureSupabaseClient
    : (SUPABASE_URL && SUPABASE_ANON_KEY)
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true
        }
    })
    : createMockClient();
